import { useLayoutEffect, useMemo, useState } from 'react'
import { Trans } from 'react-i18next'

import { IActionRepeaterValue, IActionType } from '@makyoapp/makyo-common'
import { Field } from '@makyoapp/makyo-ui'

import { useActiveLayerContext } from '@/context/__project__/ActiveLayerContext'
import { BuilderAccordionCategoryKey, useBuilderAccordionContext } from '@/context/__project__/BuilderAccordionContext'
import { useDbClientContext } from '@/context/__project__/DbClientContext'
import { useAppBuilderContext } from '@/context/AppBuilderContext'
import { useUpdateCompSettingValue } from '@/hooks/useUpdateCompSettings'
import {
  BuilderActionSetting,
  BuilderComponent,
  BuilderComponentSetting,
  BuilderDependOn,
  FlattenedItem,
} from '@/shared/builder'
import { isEmpty } from '@/shared/utils'
import { Accordion, AccordionItem } from '@/components/Accordion/Accordion'
import { ActionRepeaterV2 } from '@/components/ActionV2/ActionRepeaterV2'
import { VariableSimpleActionBody } from '@/components/VariableSelectV2/VariableSimpleAction'

function makeSureArray(value: IActionRepeaterValue | IActionRepeaterValue[]): IActionRepeaterValue[] {
  if (isEmpty(value)) return []
  return (Array.isArray(value) ? value : [value]) || []
}

/**
 * The ComponentActionField function is a component that provides an interface for editing action settings of a component in a builder.
 *
 */
function ComponentActionField({ id, isSimple, actionTypes, showLabel, dataSource, label }: BuilderActionSetting) {
  const { tables } = useDbClientContext()

  const { activeComponent, activeLayerClient, activeLayerElementSettings, isDetailPage } = useActiveLayerContext()
  const activeComponentId = activeComponent?.id || ''

  const component = activeLayerClient?.componentsMap.get(activeComponentId)

  const updateValue = useUpdateCompSettingValue(id)

  const compId = activeComponent?.compId as string

  const isList = activeComponent?.groupId.toLowerCase().includes('list')
  const isTable = activeComponent?.groupId.toLowerCase() === 'table'
  const isComment = activeComponent?.compId.toLowerCase() === 'comment'
  const isCalendar = activeComponent?.compId.toLowerCase() === 'calendar'

  const triggerSource = isList ? 'list' : compId

  // derived table data
  const dataSourceTable = useMemo(() => {
    const dataSourceValue = dataSource
      ? (activeLayerElementSettings[activeComponent?.id || '']?.[dataSource] as string)
      : undefined
    return tables.find((tb) => tb.id === dataSourceValue)
  }, [dataSource, activeLayerElementSettings, activeComponent?.id, tables])

  const componentSettings = activeLayerElementSettings?.[activeComponentId]?.[id] as
    | IActionRepeaterValue
    | IActionRepeaterValue[]

  // action value for simple action (single)
  const simpleActionValue = isEmpty(componentSettings) ? undefined : (componentSettings as IActionRepeaterValue)

  // action values for multiple
  const multipleActionValues = useMemo(() => {
    let currentValue = makeSureArray(componentSettings)

    return currentValue
  }, [componentSettings])

  const handleUpdateAction = (actionValues: IActionRepeaterValue[]) => {
    let allNoneActions = actionValues?.every((act) => act?.type === IActionType.None)
    let noAction = isEmpty(actionValues) || allNoneActions

    updateValue(actionValues.filter((act) => !!act && !isEmpty(act)))
  }

  const excludedActionTypes = useMemo(() => {
    if (!activeComponent) return []

    let excluded =
      isList || isTable || isComment || isCalendar
        ? [
            IActionType.CloseAlert,
            IActionType.PushNotification,
            IActionType.NextItem,
            IActionType.PrevItem,
            IActionType.ShowContextualMenu,
            IActionType.Logout,
            IActionType.OpenMenu,
            IActionType.OpenNavDrawer,
            IActionType.GoBack,
            IActionType.RefreshScreen,
          ]
        : isDetailPage
          ? [
              IActionType.PushNotification,
              IActionType.ShowDetailsInModal,
              IActionType.OpenNavDrawer,
              IActionType.RefreshScreen,
              IActionType.CloseAlert,
              IActionType.ShowDetailsScreen,
              IActionType.ShowContextualMenu,
            ]
          : [
              IActionType.PushNotification,
              IActionType.NextItem,
              IActionType.PrevItem,
              IActionType.RefreshScreen,
              IActionType.GoBack,
              // IActionType.UpdateRow,
              IActionType.DeleteRow,
              IActionType.DuplicateRow,
              IActionType.CloseAlert,
              IActionType.ShowContextualMenu,
              // IActionType.ToggleValue,
              // IActionType.IncrementValue,
              IActionType.RefreshScreen,
              IActionType.OpenNavDrawer,
            ]

    if (activeComponent.compId === 'fab') {
      excluded = excluded.concat([IActionType.OpenMenu, IActionType.OpenNavDrawer])
    }

    // hide Add/Update form if its not submit button
    if (!component?._IsSubmit) {
      excluded = excluded.concat([IActionType.FormAddRow, IActionType.FormUpdateRow])
    }

    // hide form update item screen if its submit button
    if (component?._IsSubmit) {
      excluded = excluded.concat([IActionType.FormUpdateItemScreen])
    }

    return excluded
  }, [activeComponent, component?._IsSubmit, isCalendar, isComment, isDetailPage, isList, isTable])

  return (
    <Field isVertical>
      {isSimple ? (
        <VariableSimpleActionBody
          id={id}
          excludeActions={excludedActionTypes}
          actionTypes={actionTypes}
          value={simpleActionValue}
          onChange={updateValue}
          showLabel={showLabel}
          tableSource={dataSourceTable}
        />
      ) : (
        <ActionRepeaterV2
          id={id}
          values={multipleActionValues}
          onChange={handleUpdateAction}
          excludeActions={excludedActionTypes}
          triggerSource={triggerSource}
          label={label}
        />
      )}
    </Field>
  )
}

interface ComponentActionPanelProps {
  comp: BuilderComponent
  ancestors: FlattenedItem[]
  actions: BuilderActionSetting[]
  name?: string
  settings?: Record<
    string,
    {
      dependOn?: BuilderDependOn
      items: BuilderComponentSetting[]
    }
  >
}

export default function ComponentActionPanel({
  actions,
  ancestors,
  name = 'action',
  settings,
}: ComponentActionPanelProps) {
  const { activeComponent, activeLayerElementSettings } = useActiveLayerContext()
  const { setAccordions, accordions } = useBuilderAccordionContext()
  const elSetting = !activeComponent ? undefined : activeLayerElementSettings[activeComponent.id]

  const { activeComponentAccordion } = useAppBuilderContext()

  const stgs = settings ? Object.entries(settings) : []

  const [localActiveItems, setLocalActiveItems] = useState<string[]>(() => {
    return activeComponentAccordion
      ? [activeComponentAccordion]
      : activeComponent && accordions?.[activeComponent.id]?.[name as BuilderAccordionCategoryKey]
        ? [accordions[activeComponent.id][name as BuilderAccordionCategoryKey]]
        : stgs.length > 0
          ? [stgs[0][0]]
          : []

    // NOTE: comment out for now
    // if (activeComponent && accordions?.[activeComponent.id]?.['action']) {
    //   return [accordions[activeComponent.id]['action']]
    // }
    // if (actions.length === 1) {
    //   return [`action-${actions[0].id}`]
    // }
    // return []
  })

  useLayoutEffect(() => {
    if (activeComponent && actions.length === 1 && !accordions?.[activeComponent.id]?.['action']) {
      const actionId = `action-${actions[0].id}`
      setAccordions('action', activeComponent.id, actionId)
      setLocalActiveItems([actionId])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeComponent?.id, actions, accordions, setAccordions])

  return (
    <Accordion
      initialActiveItems={localActiveItems}
      onSelect={(opt) => {
        if (activeComponent) {
          setAccordions('action', activeComponent.id, opt)
          setLocalActiveItems([opt])
        }
      }}
      openMultiple={false}
    >
      {actions.map((act) => {
        const showWhen = act.dependOn?.showWhen
        const dependId = act.dependOn?.id
        const shouldShow = showWhen && dependId ? showWhen(elSetting?.[dependId], activeComponent?.id, ancestors) : true

        return shouldShow ? (
          <AccordionItem key={`action-${act.id}`} itemId={`action-${act.id}`} title={<Trans>{act.label}</Trans>}>
            <ComponentActionField {...act} defaultValue={act.defaultValue} isSimple={act.isSimple} />
          </AccordionItem>
        ) : null
      })}
    </Accordion>
  )
}
